import React, { useMemo } from "react";
import SkeletonMiniStatCard from "./SkeletonMiniStatCard";

/**
 * MiniStatCard
 * - Designed to look premium in col-xl-4 (3-up) layouts
 * - Subtle gradient, soft borders, compact spacing
 * - Optional "trend" + "hint" + right-side meta badge
 */
const MiniStatCard = ({
  icon = "fa-circle",
  label = "Label",
  value = "-",
  tone = "primary", // primary | success | warning | danger | info | secondary
  hint = "",
  meta = "", // e.g. "Today", "This month"
  footer = null, // custom footer node if needed
  onClick = null,
  loading = false,
}) => {
  const toneMap = useMemo(
    () => ({
      primary: { bg: "rgba(79,70,229,0.12)", bd: "rgba(79,70,229,0.18)", text: "text-primary" },
      success: { bg: "rgba(40,167,69,0.12)", bd: "rgba(40,167,69,0.18)", text: "text-success" },
      warning: { bg: "rgba(255,193,7,0.14)", bd: "rgba(255,193,7,0.20)", text: "text-warning" },
      danger: { bg: "rgba(220,53,69,0.10)", bd: "rgba(220,53,69,0.18)", text: "text-danger" },
      info: { bg: "rgba(23,162,184,0.12)", bd: "rgba(23,162,184,0.18)", text: "text-info" },
      secondary: { bg: "rgba(108,117,125,0.12)", bd: "rgba(108,117,125,0.18)", text: "text-secondary" },
    }),
    []
  );

  const t = toneMap[tone] || toneMap.primary;

  if (loading) {
    return (
      <SkeletonMiniStatCard
        tone={tone}
        showMeta={!!meta}
        showHint={!!hint}
        showFooter={!!footer}
      />
    );
  }

  return (
    <div
      className={`card border-0 shadow-sm ${onClick ? "cursor-pointer" : ""}`}
      onClick={onClick || undefined}
      role={onClick ? "button" : undefined}
      style={{
        borderRadius: 16,
        overflow: "hidden",
        background:
          "linear-gradient(135deg, rgba(255,255,255,0.98), rgba(248,250,252,0.92))",
        border: "1px solid rgba(0,0,0,0.04)",
      }}
    >
      <div className="card-body" style={{ padding: "14px 14px 12px" }}>
        {/* Header row */}
        <div className="d-flex align-items-start justify-content-between">
          <div className="d-flex align-items-center">
            <span
              className="d-inline-flex align-items-center justify-content-center mr-3"
              style={{
                width: 44,
                height: 44,
                borderRadius: 14,
                background: t.bg,
                border: `1px solid ${t.bd}`,
              }}
            >
              <i className={`fa ${icon} ${t.text}`} style={{ fontSize: 16 }} />
            </span>

            <div className="pr-2">
              <div className="text-muted" style={{ fontSize: 12, lineHeight: 1.2 }}>
                {label}
              </div>
              <div className="font-weight-bold" style={{ fontSize: 16, lineHeight: 1.25 }}>
                {value}
              </div>
            </div>
          </div>

          {meta ? (
            <span
              className="badge badge-light"
              style={{
                borderRadius: 999,
                padding: "6px 10px",
                border: "1px solid rgba(0,0,0,0.06)",
                fontWeight: 600,
              }}
            >
              {meta}
            </span>
          ) : null}
        </div>

        {/* Hint / subtext */}
        {hint ? (
          <div className="mt-2 text-muted" style={{ fontSize: 12, lineHeight: 1.35 }}>
            {hint}
          </div>
        ) : null}

        {/* Optional footer slot */}
        {footer ? <div className="mt-2">{footer}</div> : null}
      </div>

      {/* tiny bottom accent */}
      <div
        style={{
          height: 3,
          background: t.bd,
        }}
      />
    </div>
  );
};

export default MiniStatCard;
